import { useState, useEffect, useCallback } from 'react';
import seoKeywordsService from '@/services/seoKeywords';
import { SeoTrackedKeyword, SeoPage } from '@/types/api/seo';

type GSCKeywordData = {
  dailyTrend: Array<{ date: string; clicks: number; impressions: number; ctr: number; position: number }>;
  rankingPages: Array<{ page: string; clicks: number; impressions: number; ctr: number; position: number }>;
  totals: { clicks: number; impressions: number; avgCtr: number; avgPosition: number };
};

export function useTargetPage(
  keyword: SeoTrackedKeyword | undefined,
  projectPages: SeoPage[],
  gscData: GSCKeywordData | null,
  keywordId: string | undefined,
  refetchKeyword?: () => void
) {
  const [targetPageUrl, setTargetPageUrl] = useState<string>('');

  // Derive the effective target page URL
  useEffect(() => {
    if (keyword?.targetPageId && projectPages.length > 0) {
      const page = projectPages.find((p) => p.id === keyword.targetPageId);
      if (page) {
        setTargetPageUrl(page.url);
        return;
      }
    }
    const fallback = keyword?.latestSnapshot?.url ?? gscData?.rankingPages?.[0]?.page ?? '';
    setTargetPageUrl(fallback);
  }, [keyword, projectPages, gscData]);

  const handleTargetPageChange = useCallback(
    async (pageId: string | null, pageUrl: string) => {
      if (!keywordId) return;
      try {
        await seoKeywordsService.setTargetPage(keywordId, pageId);
        setTargetPageUrl(pageUrl);
        refetchKeyword?.();
      } catch {
        /* ignore */
      }
    },
    [keywordId, refetchKeyword]
  );

  return { targetPageUrl, handleTargetPageChange };
}
